import { Data, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import ManageCheckQueryConfigComponentVM from '../Components/ManageCheckQueryConfigComponentVM';
import CheckQuerySummaryLookup from '../Models/Lookups/CheckQuerySummaryLookup';
import CheckQuerySummarySearchCriteria from '../Models/Queries/CheckQuerySummarySearchCriteria';
import ManageCheckQueryComponentVM from '../Components/ManageCheckQueryComponentVM';
import CreateCheckQueryConfigCommand from '../Models/Commands/CreateCheckQueryConfigCommand';
import CheckQueryLookup from '../Models/Lookups/CheckQueryLookup';
import CreateCheckQueryCommand from '../Models/Commands/CreateCheckQueryCommand';
import UpdateCheckQueryCommand from '../Models/Commands/UpdateCheckQueryCommand';
import UpdateCheckQueryConfigCommand from '../Models/Commands/UpdateCheckQueryConfigCommand';

@NeoModel
export default class CheckQuerySummaryVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public checkQueryApiClient = AppService.get(Types.ApiClients.CheckQueryApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public manageCheckQueryConfigComponentVM = new ManageCheckQueryConfigComponentVM();
    public manageCheckQueryComponentVM = new ManageCheckQueryComponentVM();
    public DataValidationSearchCriteria = new CheckQuerySummarySearchCriteria();
    public CheckQuerySearchCriteria = new CheckQuerySummarySearchCriteria();
    public prevCheckQueryLookupValue = new CheckQuerySummaryLookup();

    public checkQueryPageManager = new Data.PageManager(this.CheckQuerySearchCriteria, CheckQueryLookup,
        this.checkQueryApiClient.getPagedCheckQuery, {
            pageSize: 10,
            sortBy: "checkQueryCode",
            sortAscending: true,
            fetchInitial: true,
            initialTaskRunner: this.taskRunner,
        });

    public serviceProviderPageManager = new Data.PageManager(this.DataValidationSearchCriteria, CheckQuerySummaryLookup,
        this.checkQueryApiClient.getPagedCheckQuerySummary, {
            pageSize: 10,
            sortBy: "checkQueryName",
            sortAscending: true,
            fetchInitial: true,
            initialTaskRunner: this.taskRunner,
        });

    public async initialise() {
        reaction(() => this.DataValidationSearchCriteria.searchCriteria, () => this.serviceProviderPageManager.refreshData(), {fireImmediately: false, delay: 650 });
        reaction(() => this.CheckQuerySearchCriteria.searchCriteria, () => this.checkQueryPageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public cancelData() {
        this.manageCheckQueryConfigComponentVM.showManagedComponent = false;
        this.manageCheckQueryConfigComponentVM.showConfirmationModal = false;
        this.serviceProviderPageManager.refreshPage();
    }

    public cancelQuery() {
        this.manageCheckQueryComponentVM.showManagedComponent = false;
        this.checkQueryPageManager.refreshData();
    }

    public async openManagedCheckQueryConfig(checkQueryConfigLookup: CheckQuerySummaryLookup) {
        this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig = checkQueryConfigLookup;
        this.prevCheckQueryLookupValue = checkQueryConfigLookup;
        await this.manageCheckQueryConfigComponentVM.initialise();
    }
    public async openManagedCheckQuery(checkQueryLookup: CheckQueryLookup) {
        this.manageCheckQueryComponentVM.managedCheckQuery = checkQueryLookup;
        await this.manageCheckQueryComponentVM.initialise();
    }
    public async configClearSearch() {
        this.DataValidationSearchCriteria.meta.searchCriteria.value = "";
        await this.serviceProviderPageManager.refreshData();
    }

    public async checkClearSearch() {
        this.CheckQuerySearchCriteria.meta.searchCriteria.value = "";
        await this.checkQueryPageManager.refreshData();
    }

    public async triggerNextExecutionDate() {
        if (this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.triggerNextExecutionDate === true) {
            var fiveMinutesFromNow = new Date(new Date().getTime() + 5*60000);
            this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.meta.nextExecutionDateTime.value = fiveMinutesFromNow;
            this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.meta.nextExecutionDateString.value = fiveMinutesFromNow.toString();
        }
    }

    private mapCheckQueryConfigFromUpdateCommand() {
        var command = new UpdateCheckQueryConfigCommand().mapFrom(this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig);
        return command;
    }

    private mapCheckQueryConfigFromCreateCommand() {
        var command = new CreateCheckQueryConfigCommand().mapFrom(this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig);
        return command;
    }

    public async saveCheckQueryConfig() {
        this.manageCheckQueryConfigComponentVM.showConfirmationModal = false;
        this.taskRunner.run(async () => {
            await this.triggerNextExecutionDate();
            var response = this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.staticServiceProviderHouseCheckQueryId !== 0 ?
                await this.checkQueryApiClient.updateCheckQueryConfig((this.mapCheckQueryConfigFromUpdateCommand()!.toJSObject()))
                :
                await this.checkQueryApiClient.createCheckQueryConfig((this.mapCheckQueryConfigFromCreateCommand()!.toJSObject()));

            if (response.data.success) {
                await this.serviceProviderPageManager.refreshData();
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "Data validation configuration has been successfully saved.", NotificationDuration.Standard);
                if(this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.runFrequency != null)
                {
                    this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.runFrequency = this.manageCheckQueryConfigComponentVM.managedCheckQueryConfig.runFrequency / 60;
                }
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                this.serviceProviderPageManager.refreshData();
            }
            this.manageCheckQueryConfigComponentVM.showManagedComponent = false;
        })
    }

    private mapCheckQueryFromUpdateCommand() {
        var command = new UpdateCheckQueryCommand().mapFrom(this.manageCheckQueryComponentVM.managedCheckQuery);
        return command;
    }

    private mapCheckQueryFromCreateCommand() {
        var command = new CreateCheckQueryCommand().mapFrom(this.manageCheckQueryComponentVM.managedCheckQuery);
        return command;
    }

    public async saveCheckQuery() {
        this.taskRunner.run(async () => {
            await this.triggerNextExecutionDate();
            var response = this.manageCheckQueryComponentVM.managedCheckQuery.checkQueryId !== 0 ?
                await this.checkQueryApiClient.updateCheckQuery((this.mapCheckQueryFromUpdateCommand()!.toJSObject()))
                :
                await this.checkQueryApiClient.createCheckQuery((this.mapCheckQueryFromCreateCommand()!.toJSObject()));

            if (response.data.success) {
                await this.checkQueryPageManager.refreshData()
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "Check Query configuration has been successfully saved.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                await this.serviceProviderPageManager.refreshData();
            }
            this.manageCheckQueryComponentVM.showManagedComponent = false;
            
        })
    }
}